// ============================================================================
// src/shared/utils/funciones.ts
// Utilidades transversales (sin dominio): sesión (sessionStorage + AES), cifrado,
// imagen base64, y branding por CSS variables. Migrado de .js a .ts (misma lógica).
// ============================================================================
import CryptoJS from "crypto-js";
import { keyCrypto } from "../config/crypto";

interface Opcion {
  id: string | number;
  tipo: string;
  [k: string]: unknown;
}

/** Devuelve el `tipo` (label) de la opción cuyo `id` coincide con `value` (comparación por string). */
export const obtenerLabel = (
  lista: Opcion[] | null | undefined,
  value: string | number,
): string | undefined => {
  if (value !== "") {
    const label = lista?.find((d) => String(d.id) === String(value))?.tipo || "";
    return label;
  }
};

// Ordena (copia) una lista de opciones por su etiqueta visible, en orden alfabético
// (locale es, ignora may/min y respeta numeros). Defensivo ante las distintas formas
// de item del sistema (tipo/CompNom/SedDesc/nombre/label/ModDesc). Se usa en la capa
// de API de catálogos para que TODOS los selects (companía/sucursal/sede) salgan
// ordenados sin tocar cada render.
export const ordenarPorNombre = <T extends Record<string, any>>(lista: T[] | null | undefined): T[] => {
  if (!Array.isArray(lista)) return lista as any;
  const etiqueta = (o: any): string =>
    String(o?.tipo ?? o?.CompNom ?? o?.SedDesc ?? o?.nombre ?? o?.label ?? o?.ModDesc ?? "").trim();
  return [...lista].sort((a, b) =>
    etiqueta(a).localeCompare(etiqueta(b), "es", { sensitivity: "base", numeric: true }),
  );
};

// Se puede guardar tanto texto como objeto
export const guardarLocalStorage = (obj: unknown, key: string): void => {
  sessionStorage.setItem(key, JSON.stringify(obj));
};

export const obtenerLocalStorage = (key: string): any => {
  const datosLSStr = sessionStorage.getItem(key);
  const datosLSObj = JSON.parse(datosLSStr as string);
  const desencriptado = desencriptar(datosLSObj);
  return desencriptado;
};

export const limpiarLocalStorage = (key: string): void => {
  sessionStorage.removeItem(key);
};

interface NombreImagenResult {
  nombreImagen: string;
  imagenBase64: string | null;
}

export const obtenerNombreEImagenBase64 = (
  file: File | null | undefined,
): Promise<NombreImagenResult> => {
  return new Promise((resolve) => {
    const nombreImagen = file?.name || "";
    const type = file?.type || "";

    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64Value = (e.target as any)?.result as string;

        // Optimizar la imagen antes de convertirla en base64
        optimizarImagenBase64(base64Value, type)
          .then((imagenOptimizadaBase64) => {
            resolve({
              nombreImagen,
              imagenBase64: imagenOptimizadaBase64.split(",")[1],
            });
          })
          .catch((error) => {
            console.error("Error al optimizar la imagen:", error);
            resolve({
              nombreImagen,
              imagenBase64: base64Value, // En caso de error, devolver la imagen original
            });
          });
      };

      reader.readAsDataURL(file);
    } else {
      resolve({ nombreImagen, imagenBase64: null });
    }
  });
};

interface ExtensionImagenResult {
  Extension: string;
  imagenBase64: string | null;
}

export const obtenerExtensionEImagenBase64 = (
  file: File | null | undefined,
): Promise<ExtensionImagenResult> => {
  return new Promise((resolve) => {
    const partes: any = file?.name.split(".") || "";

    const Extension = partes[partes.length - 1];
    const type = file?.type || "";

    if (file) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64Value = (e.target as any)?.result as string;

        // Optimizar la imagen antes de convertirla en base64
        optimizarImagenBase64(base64Value, type)
          .then((imagenOptimizadaBase64) => {
            resolve({
              Extension,
              imagenBase64: imagenOptimizadaBase64.split(",")[1],
            });
          })
          .catch((error) => {
            console.error("Error al optimizar la imagen:", error);
            resolve({
              Extension,
              imagenBase64: base64Value, // En caso de error, devolver la imagen original
            });
          });
      };

      reader.readAsDataURL(file);
    } else {
      resolve({ Extension, imagenBase64: null });
    }
  });
};

// Función para optimizar una imagen en formato base64 utilizando canvas
const optimizarImagenBase64 = async (
  base64Data: string,
  imageType: string,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = base64Data;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;

      // Define el tamaño deseado para la imagen optimizada
      const maxWidth = 300; // Puedes ajustar esto según tus necesidades 300

      let newWidth = img.width;
      let newHeight = img.height;

      if (img.width > maxWidth) {
        newWidth = maxWidth;
        newHeight = (img.height * maxWidth) / img.width;
      }

      canvas.width = newWidth;
      canvas.height = newHeight;

      // Dibuja la imagen optimizada en el lienzo
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convierte el lienzo a base64
      const optimizadaBase64 = canvas.toDataURL(`${imageType}`, 0.7); // Puedes ajustar la calidad

      resolve(optimizadaBase64);
    };

    img.onerror = (error) => {
      reject(error);
    };
  });
};

// Obtener el elemento :root del documento
const root = document.documentElement;

/** Variable de branding: nombre de la CSS var (`--color-*`) y su valor. */
export interface ColorVar {
  name: string;
  color: string;
}

export const cambiarColores = (variables: ColorVar[]): void => {
  variables.forEach((variable) => {
    const { name, color } = variable;
    root.style.setProperty(name, color);
  });
};

export const handleImagenSeleccionada = (
  e: any,
  setLogoBase64: (valor: string | null) => void,
): void => {
  const archivo = e?.target?.files?.[0];
  if (archivo) {
    const reader = new FileReader();

    reader.onload = (ev) => {
      const imagenEnBase64 = (ev.target as any)?.result;
      setLogoBase64(imagenEnBase64);
    };

    reader.readAsDataURL(archivo);
  } else {
    setLogoBase64(null);
  }
};

export const encriptar = (objeto: unknown): string | null => {
  try {
    // Serializa el objeto a JSON antes de encriptarlo
    const objetoSerializado = JSON.stringify(objeto);
    const textoCifrado = CryptoJS.AES.encrypt(objetoSerializado, keyCrypto).toString();
    return textoCifrado;
  } catch (error) {
    return null;
  }
};

export const desencriptar = (objeto: any): any => {
  try {
    const bytes = CryptoJS.AES.decrypt(objeto, keyCrypto);
    const objetoSerializado = bytes.toString(CryptoJS.enc.Utf8);

    // Deserializa el JSON de nuevo a un objeto
    const objetoDeserializado = JSON.parse(objetoSerializado);
    return objetoDeserializado;
  } catch (error) {
    return null;
  }
};

export const obtenerFechaActual = (): string => {
  const fecha = new Date();
  const año = fecha.getFullYear();
  const mes = (fecha.getMonth() + 1).toString().padStart(2, "0");
  const dia = fecha.getDate().toString().padStart(2, "0");
  return `${año}-${mes}-${dia}`;
};
